import { startTransition, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { IStoryToon, StoryToonImage, fetchStoryToons } from '@api/webtoon';
import { QUERY_KEYS, WEBTOON_STORIES } from '@constants';
import type { WebtoonStoryMeta } from '@constants';
import type { IStory } from '@model';
import {
  getStoryToonAuthorName,
  getStoryToonCoverImage,
  getStoryToonId,
  getStoryToonSubtitle,
  getStoryToonTitle,
} from '@utils/webtoon';

export type WebtoonStoryItem = {
  webtoonId: number;
  storyId?: number;
  title: string;
  subtitle: string;
  authorName: string;
  coverImage?: IStory['coverImage'] | StoryToonImage;
  storyToon: IStoryToon;
  story?: Pick<IStory, 'id' | 'title' | 'summary' | 'coverImage'>;
  meta?: WebtoonStoryMeta;
};

type WebtoonListItem = {
  webtoonId: number;
  storyId?: number;
  storyToon: IStoryToon;
  meta?: WebtoonStoryMeta;
};

export const useWebtoonStoriesQuery = ({ enabled = true } = {}) => {
  const [isMounted, setMounted] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setMounted(true);
    });
  }, []);

  const storyToonsQuery = useQuery(
    QUERY_KEYS.WEBTOON.STORIES,
    () => fetchStoryToons(),
    {
      enabled: isMounted && enabled,
      suspense: false,
      retry: false,
      useErrorBoundary: false,
      staleTime: 1000 * 60 * 5,
    }
  );

  const webtoonListItems = useMemo<WebtoonListItem[]>(() => {
    const storyToons = storyToonsQuery.data ?? [];

    return storyToons.reduce<WebtoonListItem[]>((items, storyToon) => {
      const webtoonId = getStoryToonId(storyToon);
      if (typeof webtoonId !== 'number') {
        return items;
      }

      const storyId = storyToon.storyId ?? storyToon.story?.id;

      items.push({
        webtoonId,
        storyId,
        storyToon,
        meta: WEBTOON_STORIES.find((story) => story.id === webtoonId),
      });

      return items;
    }, []);
  }, [storyToonsQuery.data]);

  const webtoonStories = useMemo<WebtoonStoryItem[]>(() => {
    return webtoonListItems.map(({ webtoonId, storyId, storyToon, meta }) => {
      const story = storyToon.story;

      return {
        webtoonId,
        storyId,
        title: getStoryToonTitle(storyToon, meta) || story?.title || '',
        subtitle: getStoryToonSubtitle(storyToon, meta) || story?.summary || '',
        authorName: getStoryToonAuthorName(storyToon),
        coverImage: getStoryToonCoverImage(storyToon),
        storyToon,
        story,
        meta,
      };
    });
  }, [webtoonListItems]);

  return {
    isMounted,
    isLoading: enabled && (!isMounted || storyToonsQuery.isLoading),
    isError: storyToonsQuery.isError,
    webtoonStories,
    storyToonsQuery,
  };
};
